import { apiService } from './api';

// --- INTERFACES MATCHING NEW DB STRUCTURE ---

export interface SoWData {
  id: string;
  projectId: string;
  ownerId: string;
  generatedAt: string;
  version: string;
  tender_document: TenderDocumentContent;
  internal_estimates: InternalEstimates;
}

export interface TenderDocumentContent {
  title: string;
  project_summary: ProjectSummary;
  compliance_requirements: ComplianceRequirement[];
  material_schedule: MaterialScheduleItem[];
  scope_of_works: ScopeOfWorksPhase[];
}

export interface ProjectSummary {
  description: string;
  site_access_constraints: string;
  occupancy_status: string;
}

export interface ComplianceRequirement {
  certificate: string;
  trigger: string;
}

export interface MaterialScheduleItem {
  category: string;
  item_name: string;
  quantity: number;
  unit: string;
  supply_responsibility: string;
  notes: string;
}

export interface ScopeOfWorksPhase {
  phase_name: string;
  line_items: ScopeLineItem[];
  exclusions: string[];
}

export interface ScopeLineItem {
  description: string;
  pricing_note: string;
  quantifiable_unit: string;
  quantity: number;
}

export interface InternalEstimates {
  total_person_days: number;
  project_duration_weeks_range: string;
  phase_breakdowns: PhaseBreakdown[];
}

export interface PhaseBreakdown {
  phase_name: string;
  estimated_person_days: number;
  rationale: string;
}

// Interface for the Status Polling
export interface SoWStatus {
  sowId: string;
  status: 'generating' | 'completed' | 'failed';
  progress: number;
  estimatedCompletion: string;
  errorMessage?: string;
}

class SoWService {
  // Generate SoW for project
  async generateSoW(projectId: string): Promise<{ sowId: string; status: string }> {
    return apiService.post(`/projects/${projectId}/sow/generate`, {});
  }

  // Get SoW generation status
  async getSoWStatus(projectId: string, sowId: string): Promise<SoWStatus> {
    return apiService.get(`/projects/${projectId}/sow/${sowId}/status`);
  }

  // Get completed SoW
  async getSoW(projectId: string, sowId: string): Promise<SoWData> {
    return apiService.get(`/projects/${projectId}/sow/${sowId}`);
  }

  // Update SoW (for future editing capabilities)
  async updateSoW(projectId: string, sowId: string, updates: Partial<TenderDocumentContent>): Promise<SoWData> {
    return apiService.put(`/projects/${projectId}/sow/${sowId}`, { tender_document: updates });
  }

  // Accept SoW and update project status
  async acceptSoW(projectId: string, sowId: string): Promise<{ success: boolean }> {
    return apiService.post(`/projects/${projectId}/sow/${sowId}/accept`, {});
  }

  // Format currency for display
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  }

  // Format date for display
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
}

export const sowService = new SoWService();